import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { IndividualAirflowJob } from '../pages/IndividualJob';
import Plot from 'react-plotly.js';
import { PlotlyHTMLElement } from 'plotly.js';
import { generateRandomString } from '../../../utils/formattingFns';

export const statusColors = {
    success: '#7BC960',  // green
    running: '#FFA461',  // orange
    failed: '#FF8484',   // red
    fail: '#FF8484',   // red
    skipped: '#FFF278',  // blue
    null: '#cccccc'      // default/unknown
};

export default function GanttChart({
    job,
    tasks,
    setSelectedRun,
}: {
    job: IndividualAirflowJob | null,
    tasks: any[],
    setSelectedRun: Dispatch<SetStateAction<{
        dag_id: string,
        task_id?: string,
        map_index?: number
    } | null>>,
}) {
    const [ data, setData ] = useState<Plotly.Data[]>([]);
    const [ layout, setLayout ] = useState<Partial<Plotly.Layout> | null>(null);
    const [ categoryVals, setCategoryVals ] = useState<string[]>([]);

    const getYAxis = (start: number, end: number, numElements: number) => {
        let arr = [];
        const step = (end - start) / (numElements - 1);

        for (let i = 0; i < numElements; i++) {
            arr.push(start + step * i);
        }

        return arr;
    }
    

    // const config: Partial<Config> = {
    //     scrollZoom: false,
    //     displaylogo: false,
    //     displayModeBar: true,
    //     responsive: true,
    //     modeBarButtonsToRemove: ['toggleSpikelines', 'resetScale2d', 'hoverCompareCartesian', 'hoverClosestCartesian'],
    //     toImageButtonOptions: {
    //         format: 'svg',
    //         scale: 1,
    //     }
    // }

    function formatMilliseconds(ms: number) {
        const totalSeconds = Math.floor(ms / 1000);
        const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
        const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
        const seconds = String(totalSeconds % 60).padStart(2, '0');
      
        return `${hours}:${minutes}:${seconds}`;
    }

    useEffect(() => {
        if (job && job.end_date && tasks.length > 0) {
            const endDate = new Date(job.end_date.replace(/\.\d+/, '').replace(/(\+\d{2}:\d{2})/, 'Z'));
            const startDate = new Date(job.start_date.replace(/\.\d+/, '').replace(/(\+\d{2}:\d{2})/, 'Z'));

            const duration = endDate.getTime() - startDate.getTime();
            let marks = 0;
            if (duration % 2 === 0) {
                marks = 5
            } else {
                marks = 6;
            };


            const tickvals = getYAxis(0, duration, marks);

            let ticktext: string[] = [];

            tickvals.forEach((val, i) => {
                // const time = startDate.getTime() + val;
                // const tickDate = new Date(time);
                // ticktext.push(tickDate.toLocaleString());
                if (i === 0 || i === tickvals.length-1) {
                    ticktext.push('')
                } else {
                    ticktext.push(formatMilliseconds(val))
                }
            })


            const placeBarGap = () => {
                return {
                    type: 'bar',
                    x: [0],
                    y: [generateRandomString()],
                    orientation: 'h',
                    name: '',
                    base: 0,
                    width: 0.5,
                }
            }

            let traces: any[] = [
                placeBarGap(),
                {
                    type: 'bar',
                    x: [0],
                    y: ['DAG'],
                    orientation: 'h',
                    name: 'DAG',
                    base: 0,
                    width: 1,
                },
                placeBarGap(),
                {
                    type: 'bar',
                    x: [duration],
                    y: [job.dag_id],
                    orientation: 'h',
                    name: job.dag_id,
                    base: 0,
                    marker: {
                        color: statusColors[job.state as keyof object]
                    },
                    width: 1,
                },
                placeBarGap(),

            ];

            let taskTraces: any[] = [
                {
                    type: 'bar',
                    x: [0],
                    y: ['Tasks'],
                    orientation: 'h',
                    name: 'Tasks',
                    base: 0,
                    width: 1,
                },
                placeBarGap(),
            ];
            let mTraces: any[] = [];
            let total = 0

            let annotations: any[] = [
                {
                    x: 0,
                    y: 'DAG',
                    xanchor: 'right',
                    yanchor: 'middle',
                    showarrow: false,
                    text: 'DAG',
                    font: {
                        family: 'Montserrat',
                    }
                },
                {
                    x: 0,
                    y: job.dag_id,
                    xanchor: 'right',
                    yanchor: 'middle',
                    showarrow: false,
                    text: job.dag_id,
                    captureevents: true,
                    font: {
                        family: 'Montserrat',
                    }
                },
                {
                    x: 0,
                    y: 'Tasks',
                    xanchor: 'right',
                    yanchor: 'middle',
                    showarrow: false,
                    text: 'Tasks',
                    font: {
                        family: 'Montserrat',
                    }
                },
            ];

            let yVals: string[] = ['DAG', job.dag_id, 'Tasks'];

            const truncateLabel = (label: string, max = 30) =>
                label.length > max ? label.slice(0, max) + '…' : label;

            tasks.forEach((task,i) => {
                const { start_date, end_date, task_id, state, map_index } = task.task_instance; 
                if (start_date && end_date) {
                    const taskStartDate = new Date(start_date.replace(/\.\d+/, '').replace(/(\+\d{2}:\d{2})/, 'Z'));
                    const taskEndDate = new Date(end_date.replace(/\.\d+/, '').replace(/(\+\d{2}:\d{2})/, 'Z'));
                    const taskDuration = taskEndDate.getTime() - taskStartDate.getTime();
    
                    const taskTrace: any = {
                        type: 'bar',
                        x: [taskDuration],
                        y: [`${task_id}_${map_index}`],
                        orientation: 'h',
                        base: taskStartDate.getTime() - startDate.getTime(),
                        marker: {
                            color: statusColors[state as keyof object], 
                        },
                        width: 1,
                        
                    }

                    annotations.push({
                        x: 0,
                        y: `${task_id}_${map_index}`,
                        xanchor: 'right',
                        yanchor: 'middle',
                        showarrow: false,
                        text: truncateLabel(`${task_id}_${map_index}`),
                        captureevents: true,
                        font: {
                            family: 'Montserrat',
                        }
                    })

                    yVals.push(`${task_id}_${map_index}`);

                    if (task_id !== 'megamodels_v2-train_nestedcv_v0.0.0') {
                        // taskTraces.push(taskTrace);
                        taskTraces = taskTraces.concat([taskTrace, placeBarGap()])
                    } else {
                        mTraces = mTraces.concat([taskTrace, placeBarGap()])
                        // mTraces.push(taskTrace);
                        total = total + taskDuration
                    }


                };
            });

            const allTraces = traces.concat(taskTraces).concat(mTraces);

            const categoryArr = allTraces.map(trace => trace.y[0]);
            setCategoryVals(categoryArr);

            let shapes: any[] = [];
            categoryArr.forEach(cat => {
                if (!yVals.includes(cat)) {
                    shapes.push({
                        type: 'line',
                        xref: 'paper',
                        yref: 'y',
                        x0: 0,
                        x1: 0.95,
                        y0: cat,
                        y1: cat,
                        line: {
                            color: '#ccc',
                            width: 1,
                        }
                    });
                }
            });

            shapes.push({
                type: 'rect',
                xref: 'paper',
                yref: 'y',
                x0: 0,
                x1: 0.95,
                y0: categoryArr[2],
                y1: categoryArr[4],
                fillcolor: 'rgb(173, 216, 230)',
                opacity: 0.3,
                line: { width: 0 },
                layer: 'above',
            })

            shapes.push({
                type: 'rect',
                xref: 'paper',
                yref: 'y',
                x0: 0,
                x1: 0.95,
                y0: categoryArr[0],
                y1: categoryArr[2],
                fillcolor: '#ccc',
                opacity: 0.4,
                line: { width: 0 },
                layer: 'below',
            })

            shapes.push({
                type: 'rect',
                xref: 'paper',
                yref: 'y',
                x0: 0,
                x1: 0.95,
                y0: categoryArr[4],
                y1: categoryArr[6],
                fillcolor: '#ccc',
                opacity: 0.4,
                line: { width: 0 },
                layer: 'below',
            })

            const plotLayout: Partial<Plotly.Layout> = {
                barmode: 'overlay',
                bargap: 1,
                xaxis: {
                    tickvals,
                    ticktext,
                    side: 'top',
                    // tickangle: -45,
                    showgrid: true,
                    gridwidth: 1,
                    gridcolor: '#ccc', 
                    automargin: true,
                    tickfont: {
                        family: 'Montserrat',
                    }
                },
                yaxis: {
                    type: 'category',
                    categoryorder: 'array',
                    categoryarray: categoryArr,
                    tickvals: categoryArr,
                    autorange: 'reversed',
                    showticklabels: false,
                    automargin: true,
                    // showgrid: true,
                    // gridwidth: 1,
                    // gridcolor: '#ccc', 
                },
                shapes,
                annotations,
                plot_bgcolor: 'rgba(0,0,0,0)',   // makes plot area transparent
                paper_bgcolor: 'rgba(0,0,0,0)',
                showlegend: false,
                autosize: true,
                width: 700,
                margin: {
                    l: 0, 
                    r: 0,
                    t: 40,
                    b: 40,
                },
            }


            setData(allTraces);
            setLayout(plotLayout);

        }
    }, [job, tasks]);


    const handleAnnotationClick = (e:any) => {
        if (e && e.annotation && tasks.length > 0 && job && categoryVals.length > 0 && layout !== null) {
            const yVal = e.annotation.y;

            if (yVal) {
                let shape: any = {
                    type: 'rect',
                    xref: 'paper',
                    yref: 'y',
                    x0: 0,
                    x1: 0.95,
                    y0: '',
                    y1: '',
                    fillcolor: 'rgb(173, 216, 230)',
                    opacity: 0.3,
                    line: { width: 0 },
                    layer: 'above',
                };

                const splitY = (yVal as string).split('_');

                let mapIndex: number | undefined = undefined;
                let taskOrJobName: string = '';


                if (!isNaN(Number(splitY[splitY.length-1]))) {
                    mapIndex = Number(splitY[splitY.length-1]);
                    taskOrJobName = (splitY.filter((y,i) => i !== splitY.length-1)).join('_');
                } else {
                    taskOrJobName = yVal;
                }

                const index = categoryVals.indexOf(yVal);

                if (index !== -1) {
                    shape = {
                        ...shape,
                        y0: categoryVals[index - 1],
                        y1: categoryVals[index + 1] 
                    };
                }

                let taskOrJob: {
                    dag_id: string,
                    task_id?: string,
                    map_index?: number
                } | null = null;


                tasks.forEach(task => {
                    const taskInstance = task.task_instance;
                    if (taskInstance.task_id === taskOrJobName) {
                        if (mapIndex !== undefined) {
                            if (taskInstance.map_index === mapIndex) {
                                taskOrJob = {
                                    dag_id: taskInstance.dag_id,
                                    task_id: taskInstance.task_id,
                                    map_index: taskInstance.map_index,
                                };
                            }
                        } else {
                            taskOrJob = {
                                dag_id: taskInstance.dag_id,
                                task_id: taskInstance.task_id,
                            };
                        }
                    }
                });

                if (taskOrJob === null) {
                    taskOrJob = {
                        dag_id: job.dag_id
                    }
                };

                if (shape.y0 && shape.y1) {
                    const layoutShapes = layout.shapes;

                    if (layoutShapes) {
                        const updatedShapes = layoutShapes.map(s => {
                            if (s.type === 'rect' && s.fillcolor !== undefined && s.fillcolor !== '#ccc') {
                                return {...shape}
                            } else {
                                return {...s}
                            }
                        });

                        setLayout({
                            ...layout,
                            shapes: updatedShapes,
                        })
                    }
                }

                setSelectedRun(taskOrJob);

            };
        };
    };

    return (
        <>
        {data.length > 0 && layout ? (
            <div className="w-full max-w-[700px] flex flex-col justify-start items-stretch gap-2">
                {/* <div className="flex justify-between items-center gap-4">
                    <div className="flex justify-start items-center gap-1">
                        <div className="w-[20px] text-default"><GanttIcon /></div>
                        <p className="text-[20px] font-semibold text-default">Gantt Chart</p>
                    </div>
                </div> */}
                
                {data.length > 0 && layout && (
                    <Plot
                        data={data} 
                        layout={layout} 
                        className='w-full max-h-[600px] h-full rounded-lg overflow-hidden'
                        // config={config}
                        onClickAnnotation={handleAnnotationClick}
                        onInitialized={(_, gd) => {
                            const plotDiv = gd as PlotlyHTMLElement;
                            plotDiv.on('plotly_clickannotation', handleAnnotationClick);
                        }}
                    />
                )}
            </div>
        ) : (
            <></>
        )}
        </>
    )
}